/**
 * M5 — the pure "should I fire the Get Found prewarm?" decision, kept
 * framework-free (no React, no fetch) so it is unit-testable standalone, the
 * same house idiom onboardingStep.ts/jobBuckets.ts/stageMove.ts already
 * establish for this app's pure modules. usePresencePrewarm.ts is the thin,
 * untested wire (mirrors useOnboardingGate.ts's own split: the decision is
 * pure + tested, the hook that calls fetch is not).
 *
 * The condition mirrors apps/web/src/pages/Presence.tsx's own gate
 * (`if (roles.length === 0) return` — R125) PLUS the corpus-side half the
 * task brief asks for ("a user with roles>0"): a fresh sign-up with zero
 * corpus roles and/or zero saved target roles has nothing for the bridge to
 * warm (POST /presence/prewarm's own `empty_corpus` fail-quiet would no-op
 * anyway), so this gate skips the round trip entirely rather than firing a
 * request the server will just decline.
 */

export interface PrewarmGateInput {
  /** GET /corpus/roles' result length. Null while the read is still in
   *  flight or unknown (a failed fetch) — treated as "not yet ready," never
   *  coerced to 0 (mirrors onboardingStep.ts's own null-handling discipline). */
  corpusRoleCount: number | null;
  /** GET /presence/target-roles' result (user_profiles.attributes.desiredRoles).
   *  Null while still loading/unknown; empty array is a real "nothing saved yet." */
  targetRoles: string[] | null;
}

/** True only when there is a real corpus (roles>0) AND a saved target-role
 *  family to bridge against — the exact two things POST /presence/prewarm
 *  needs to do useful work (see presence.routes.ts's own `empty_corpus` /
 *  `targetRoleFamily` handling). Either signal still loading/unknown
 *  (null) or genuinely empty ([] / 0) both resolve to false — never fires
 *  speculatively on partial/unknown state. */
export function shouldFirePresencePrewarm(input: PrewarmGateInput): boolean {
  const { corpusRoleCount, targetRoles } = input;
  if (corpusRoleCount === null || corpusRoleCount <= 0) return false;
  if (targetRoles === null || targetRoles.length === 0) return false;
  return true;
}
